import { doc, runTransaction, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  FarkleAction,
  FarkleActionPayloadMap,
  FarkleActionType,
  FarkleGame,
  FarklePhase,
  FarkleSettings,
  FarkleStateDocument,
  PlayerId,
} from './logic';

const stateDocRef = (partyId: string) => doc(db, 'parties', partyId, 'farkle', 'state');

const uuid = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : Math.random().toString(36).slice(2));
const now = () => Date.now();

const derivePhase = (game: FarkleGame): FarklePhase => game.getPhase();

const buildDoc = (
  partyId: string,
  gameId: string,
  game: FarkleGame,
  settings: FarkleSettings,
  computerDice: boolean,
  createdAt?: number,
): FarkleStateDocument => ({
  partyId,
  gameId,
  phase: derivePhase(game),
  createdAt: createdAt ?? now(),
  updatedAt: now(),
  settings,
  state: game.serialize(),
  computerDice,
});

export const startFarkleGame = async (
  partyId: string,
  players: PlayerId[],
  options?: { settings?: Partial<FarkleSettings>; computerDice?: boolean }
): Promise<FarkleStateDocument> => {
  if (!players.length) throw new Error('At least one player required');
  const computerDice = !!options?.computerDice;
  const settings = { ...FarkleGame.DEFAULT_SETTINGS, ...(options?.settings || {}) };
  const ref = stateDocRef(partyId);
  const gameId = uuid();
  return runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) {
      const existingDoc = existing.data() as FarkleStateDocument;
      if (existingDoc.phase !== 'complete') {
        throw new Error('Farkle game already active');
      }
    }
    const game = new FarkleGame(players, settings);
    const docData = buildDoc(partyId, gameId, game, settings, computerDice);
    tx.set(ref, docData);
    return docData;
  });
};

export const fetchFarkleState = async (partyId: string): Promise<FarkleStateDocument | null> => {
  const snap = await getDoc(stateDocRef(partyId));
  return snap.exists() ? (snap.data() as FarkleStateDocument) : null;
};

const applyGameMutation = async <T>(
  partyId: string,
  mutate: (docData: FarkleStateDocument, game: FarkleGame) => T
): Promise<{ doc: FarkleStateDocument; result: T }> => {
  const ref = stateDocRef(partyId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error('No active Farkle game');
    }
    const docData = snap.data() as FarkleStateDocument;
    const game = FarkleGame.fromSnapshot(docData.state, docData.settings);
    const result = mutate(docData, game);
    const updatedDoc = {
      ...docData,
      phase: derivePhase(game),
      updatedAt: now(),
      settings: docData.settings,
      state: game.serialize(),
    } satisfies FarkleStateDocument;
    tx.set(ref, updatedDoc);
    return { doc: updatedDoc, result };
  });
};

export const rollDice = async (
  partyId: string,
  playerId: PlayerId,
  options?: { dice?: number[]; rng?: () => number }
): Promise<number[]> => {
  const { result } = await applyGameMutation(partyId, (docData, game) => {
    if (game.getCurrentPlayer() !== playerId) {
      throw new Error('Not your turn');
    }
    if (docData.computerDice || !options?.dice) {
      return game.roll(options?.rng);
    }
    return game.manualRoll(options.dice);
  });
  return result;
};

export const selectDice = async (
  partyId: string,
  playerId: PlayerId,
  indices: number[]
): Promise<{ tentativeScore: number; hotDice: boolean }> => {
  const { result } = await applyGameMutation(partyId, (_, game) => {
    if (game.getCurrentPlayer() !== playerId) {
      throw new Error('Not your turn');
    }
    const outcome = game.selectDice(indices);
    if ('error' in outcome) {
      throw new Error(outcome.error);
    }
    return outcome;
  });
  return result;
};

export const bankScore = async (partyId: string, playerId: PlayerId): Promise<FarkleStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (_, game) => {
    if (game.getCurrentPlayer() !== playerId) {
      throw new Error('Not your turn');
    }
    game.bank();
  });
  return doc;
};

export const endFarkleGame = async (partyId: string): Promise<void> => {
  await runTransaction(db, async (tx) => {
    const ref = stateDocRef(partyId);
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const docData = snap.data() as FarkleStateDocument;
    const game = FarkleGame.fromSnapshot(docData.state, docData.settings);
    game.gameOver = true;
    game.lastRound = false;
    game.finalRoundPlayers = new Set();
    const updated: FarkleStateDocument = {
      ...docData,
      phase: 'complete',
      updatedAt: now(),
      state: game.serialize(),
    };
    tx.set(ref, updated);
  });
};

export const resetFarkleGameState = async (partyId: string): Promise<void> => {
  await deleteDoc(stateDocRef(partyId));
};

export const logAction = async <T extends FarkleActionType>(
  partyId: string,
  action: FarkleAction<T>
): Promise<void> => {
  const ref = doc(db, 'parties', partyId, 'farkle_actions', action.createdAt.toString());
  await setDoc(ref, action as FarkleAction);
};
